// Testers list page — kind + status filters; account_count column.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Card, CardBody } from '@/components/ui/card';
import { lookups, testers } from '@/lib/api';
import { useMe } from '@/hooks/useMe';
import type { TesterKind } from '@frt/shared-types';

function displayFor(t: {
  kind: TesterKind;
  short_name: string | null;
  short_business_name: string | null;
  business_name: string | null;
  first_name: string | null;
  last_name: string | null;
}): string {
  if (t.kind === 'person') {
    return t.short_name ?? `${t.first_name ?? ''} ${t.last_name ?? ''}`.trim();
  }
  return t.short_business_name ?? t.business_name ?? '(unnamed business)';
}

export function TestersListPage() {
  const { data: me } = useMe();
  const canEdit = me?.role === 'admin' || me?.role === 'senior';
  const canSuppress = me?.role === 'admin';

  const [kind, setKind] = useState<TesterKind | ''>('');
  const [statusName, setStatusName] = useState<string>(''); // 'Active' | 'Inactive' | ''

  const { data: testerStatusOptions } = useQuery({
    queryKey: ['lookups', 'tester-status-options'],
    queryFn: () => lookups.testerStatusOptions.list(),
  });
  const statusIdByName = (name: string): number | undefined =>
    testerStatusOptions?.data.find((s) => s.name === name)?.id;
  const selectedStatusId = statusName ? statusIdByName(statusName) : undefined;

  const { data, isLoading, error } = useQuery({
    queryKey: ['testers', { kind, statusName }],
    queryFn: () =>
      testers.list({
        ...(kind ? { kind: kind as TesterKind } : {}),
        ...(selectedStatusId ? { tester_status_id: selectedStatusId } : {}),
        per_page: 200,
      }),
    enabled: !statusName || selectedStatusId !== undefined,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">Testers</h1>
        {canEdit && (
          <Link to="/testers/new" className="inline-block">
            <Button>New Tester</Button>
          </Link>
        )}
      </div>
      <div className="flex gap-3 items-center text-sm">
        <label>
          <span className="mr-2 text-slate-600">Kind</span>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as TesterKind | '')}
            className="rounded border border-slate-300 px-2 py-1"
          >
            <option value="">All</option>
            <option value="person">Person</option>
            <option value="business">Business</option>
          </select>
        </label>
        <label>
          <span className="mr-2 text-slate-600">Status</span>
          <select
            value={statusName}
            onChange={(e) => setStatusName(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1"
          >
            <option value="">All</option>
            {(testerStatusOptions?.data ?? [])
              .filter((s) => s.active)
              .map((s) => (
                <option key={s.id} value={s.name}>
                  {s.name}
                </option>
              ))}
          </select>
        </label>
      </div>
      <Card>
        <CardBody>
          {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}
          {isLoading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-slate-500">
                <tr>
                  <th className="py-1 pr-3">Name</th>
                  <th className="py-1 pr-3">Kind</th>
                  <th className="py-1 pr-3">Status</th>
                  <th className="py-1 pr-3">Accounts</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(data?.data ?? []).map((t) => (
                  <tr key={t.id} className="border-t border-slate-100">
                    <td className="py-1 pr-3">
                      <Link
                        to="/testers/$id"
                        params={{ id: String(t.id) }}
                        className="text-brand-700 hover:underline"
                      >
                        {displayFor(t)}
                      </Link>
                    </td>
                    <td className="py-1 pr-3">{t.kind}</td>
                    <td className="py-1 pr-3">{t.tester_status_name ?? '—'}</td>
                    <td className="py-1 pr-3">{t.account_count}</td>
                    <td className="py-1 text-right">
                      {canSuppress && (
                        <SuppressButton
                          id={t.id}
                          isInactive={t.tester_status_name === 'Inactive'}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function SuppressButton({ id, isInactive }: { id: number; isInactive: boolean }) {
  const queryClient = useQueryClient();
  const action = useMutation({
    // Per ops feedback (mig 0010): Inactive is the suppression mechanism.
    mutationFn: () => (isInactive ? testers.restore(id) : testers.suppress(id)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['testers'] }),
  });
  return (
    <Button
      size="sm"
      variant={isInactive ? 'secondary' : 'destructive'}
      onClick={() => action.mutate()}
      disabled={action.isPending}
    >
      {isInactive ? 'Reactivate' : 'Mark Inactive'}
    </Button>
  );
}
